import {
    createContext,
    useContext,
    PropsWithChildren,
    useState,
    useEffect,
    useCallback
} from "react";

import {
    calculateProductPricing,
    CalculateProductPricingResult,
    getProductCalculationInputs
} from "./calculate";
import { Config, InputValues } from "./common.ts";

const PriceFinderContext = createContext<PriceFinderContextValue | undefined>(
    undefined
);

export interface PriceFinderContextValue {
    inputValues: InputValues;
    changeInputValues: (
        inputValues: Partial<InputValues>
    ) => CalculateProductPricingResult;
    calculationResult: CalculateProductPricingResult;
    config: Config;
}

export const usePriceFinderContext = () => {
    const priceFinderContext = useContext(PriceFinderContext);
    if (!priceFinderContext) {
        throw new Error(
            "usePriceFinderContext must be used within a PriceFinderProvider"
        );
    }
    return priceFinderContext;
};

export interface PriceFinderContextProviderProps extends PropsWithChildren {
    config: Config;
    inputValues: InputValues;
}
export const PriceFinderContextProvider = (
    props: PriceFinderContextProviderProps
) => {
    const [inputValues, setInputValues] = useState<InputValues>(
        props.inputValues
    );

    const calculate = useCallback(
        (v: InputValues) => {
            const { config } = props;
            const productCalculationInputs = getProductCalculationInputs(
                config.planLimitConfigs,
                config.unitMinEnterprise,
                v.unitCount,
                v.billingCycle
            );

            return calculateProductPricing(
                config.ccpPricingData,
                productCalculationInputs,
                config.currencyISOCode,
                config.localeISOCode
            );
        },
        [props]
    );

    const [calculationResult, setCalculationResult] =
        useState<CalculateProductPricingResult>(() => calculate(inputValues));

    // Recalculate when config (including currency) changes
    useEffect(() => {
        const result = calculate(inputValues);
        setCalculationResult(result);
    }, [props.config, inputValues, calculate]);

    const changeInputValues = (
        changes: Partial<InputValues>
    ): CalculateProductPricingResult => {
        const nextValues = {
            ...inputValues,
            ...changes
        };
        setInputValues(nextValues);
        const result = calculate(nextValues);
        setCalculationResult(result);
        return result;
    };

    return (
        <PriceFinderContext.Provider
            value={{
                inputValues,
                changeInputValues,
                calculationResult,
                config: props.config
            }}
        >
            {props.children}
        </PriceFinderContext.Provider>
    );
};
